import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateCalendar } from "../dates/DateCalendar";
import { useCities } from "../../contexts/CitiesProvider";
import type { ChoiceRow } from "../../lib/forms/fieldOptions";
import { labeledChoiceFromRows } from "../../lib/forms/fieldOptions";
import {
  displayDateToIso,
  displayTimeToHm,
  formatIsoDate,
  isoDatePart,
  isoToDisplayDate,
  isoToDisplayTime,
  maskDisplayDate,
  maskDisplayTime,
} from "../../lib/dates/isoDate";
import {
  formatNationalPhoneInput,
  formatNationalPhonePart,
  formatPhoneNumber,
  parseStoredPhoneValue,
} from "../../lib/forms/phoneFormat";
import { reportFieldValue } from "../../lib/forms/reportFieldValue";
import { normalizeDateTimeSubmit } from "../../lib/forms/dateField";
import type { FormFieldInputProps, FormFieldRef } from "./types";

const slotIdleClass =
  "inline cursor-pointer border-b-2 border-blue-300 px-1 pb-0.5 text-blue-700 transition-colors hover:border-blue-400 hover:text-blue-800";

const slotInvalidClass =
  "inline cursor-pointer border-b-2 border-red-400 px-1 pb-0.5 text-red-700";

const slotInputClass =
  "inline min-w-[4ch] border-0 border-b-2 border-blue-600 bg-transparent px-1 pb-0.5 font-inherit text-inherit outline-none";

type CityOption = { id: string; name: string };

interface InlineFieldControlProps extends FormFieldInputProps {
  label: string;
  labelMuted?: boolean;
  fieldKind?:
    | "text"
    | "email"
    | "phone"
    | "date"
    | "birthdate"
    | "city"
    | "choice";
  choiceRows?: ChoiceRow[];
  validation?: (value: string) => boolean;
  withTime?: boolean;
  min?: string;
  max?: string;
  disabled?: boolean;
  emptyHint?: string;
}

export const InlineFieldControl = forwardRef<
  FormFieldRef,
  InlineFieldControlProps
>((props, ref) => {
  const {
    label,
    labelMuted = false,
    fieldId,
    fieldName,
    onValueChange,
    fieldKind = "text",
    choiceRows = [],
    validation,
    withTime = false,
    min,
    max,
    disabled = false,
    emptyHint,
  } = props;

  const wrapperRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const listId = useId();
  const {
    cities: allCities,
    isLoading: citiesLoading,
    error: citiesError,
    retry: retryCities,
  } = useCities();

  const [active, setActive] = useState(false);
  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState<CityOption[]>([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  const [timeDraft, setTimeDraft] = useState("");

  const isChoice = fieldKind === "choice";
  const isCity = fieldKind === "city";
  const isPhone = fieldKind === "phone";
  const isDateField = fieldKind === "date" || fieldKind === "birthdate";
  const usesCityChoices = choiceRows.length > 0;
  const cityCatalogLoading = isCity && !usesCityChoices && citiesLoading;
  const cityCatalogError = isCity && !usesCityChoices ? citiesError : null;

  const cityCatalog = useMemo((): CityOption[] => {
    if (choiceRows.length > 0) {
      return choiceRows.map((row) => ({ id: row.value, name: row.label }));
    }
    return allCities.map((city) => ({
      id: String(city.id),
      name: city.name ?? "",
    }));
  }, [allCities, choiceRows]);

  const inputType =
    fieldKind === "email"
      ? "email"
      : fieldKind === "phone"
        ? "tel"
        : fieldKind === "date" && withTime
          ? "datetime-local"
          : fieldKind === "birthdate" || fieldKind === "date"
            ? "date"
            : "text";

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(fieldName, next, onValueChange);
  };

  const clear = () => {
    commitValue("");
    setSearchTerm("");
  };

  const validate = () => {
    if (!validation) return true;
    const result = validation(value);
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    fieldId,
    fieldName,
    labeledChoice: isChoice ? labeledChoiceFromRows(choiceRows, value) : null,
    scrollToElement: (options) => {
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    validate,
    clear,
  }));

  useEffect(() => {
    if (!isChoice) return;
    if (value && !choiceRows.some((row) => row.value === value)) {
      commitValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when options no longer contain value
  }, [choiceRows, isChoice, value]);

  useEffect(() => {
    if (disabled) setActive(false);
  }, [disabled]);

  useEffect(() => {
    if (!active) return;
    inputRef.current?.focus();
    if (isDateField) {
      setDateDraft(isoToDisplayDate(value));
      setTimeDraft(withTime && value ? isoToDisplayTime(value) : "");
    }
  }, [active]);

  useEffect(() => {
    if (!isCity || !cityOpen) return;
    if (cityCatalogLoading || cityCatalogError) {
      setSearchResult([]);
      return;
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setSearchResult([]);
      return;
    }
    setSearchResult(
      cityCatalog
        .filter((city) => city.name.toLowerCase().includes(term))
        .slice(0, 8),
    );
  }, [
    cityCatalog,
    cityCatalogError,
    cityCatalogLoading,
    cityOpen,
    isCity,
    searchTerm,
  ]);

  const closeEditor = () => {
    setActive(false);
    setCityOpen(false);
    validate();
  };

  const displayLabel = label.trim() || "поле";
  const showMutedLabel = labelMuted || !label.trim();
  const cityDisplayName =
    cityCatalog.find((city) => city.id === value)?.name ?? value;
  const dateDisplayValue = value
    ? withTime
      ? `${isoToDisplayDate(value)} ${isoToDisplayTime(value)}`
      : isoToDisplayDate(value)
    : "";
  const displayValue = isChoice
    ? (choiceRows.find((row) => row.value === value)?.label ?? value)
    : isCity
      ? cityDisplayName
      : isPhone && value
        ? formatPhoneNumber(value)
        : isDateField
          ? dateDisplayValue
          : value;

  if (!active) {
    const idleText = displayValue || emptyHint || displayLabel;
    return (
      <span
        ref={wrapperRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={[
          disabled
            ? "inline cursor-not-allowed border-b-2 border-slate-200 px-1 pb-0.5 text-slate-400"
            : validationState
              ? slotIdleClass
              : slotInvalidClass,
          !disabled && showMutedLabel && !displayValue ? "text-slate-400" : "",
        ].join(" ")}
        onClick={() => {
          if (!disabled) setActive(true);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setActive(true);
          }
        }}
      >
        {idleText}
      </span>
    );
  }

  if (isChoice) {
    return (
      <span ref={wrapperRef} className="relative inline">
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={value}
          disabled={disabled}
          className={slotInputClass}
          onChange={(event) => {
            setValidationState(true);
            const next =
              inputType === "datetime-local"
                ? normalizeDateTimeSubmit(event.target.value)
                : event.target.value;
            commitValue(next);
          }}
          onBlur={closeEditor}
        >
          <option value="">{emptyHint || displayLabel}</option>
          {choiceRows.map((row) => (
            <option key={row.value} value={row.value}>
              {row.label}
            </option>
          ))}
        </select>
      </span>
    );
  }

  if (isCity) {
    return (
      <span ref={wrapperRef} className="relative inline">
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={searchTerm}
          placeholder={displayLabel}
          className={slotInputClass}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={cityOpen}
          onChange={(event) => {
            setValidationState(true);
            setSearchTerm(event.target.value);
            setCityOpen(true);
            commitValue("");
          }}
          onFocus={() => {
            if (!searchTerm && value) {
              setSearchTerm(cityDisplayName);
            }
            setCityOpen(true);
          }}
          onBlur={() => {
            window.setTimeout(() => {
              const match = cityCatalog.find(
                (city) =>
                  city.name.toLowerCase() === searchTerm.trim().toLowerCase(),
              );
              if (match) {
                commitValue(match.id);
                setSearchTerm(match.name);
              }
              closeEditor();
            }, 120);
          }}
        />
        {cityOpen &&
        (cityCatalogLoading ||
          cityCatalogError ||
          searchResult.length > 0 ||
          searchTerm.trim()) ? (
          <div
            id={listId}
            className="absolute left-0 top-full z-20 mt-1 max-h-40 min-w-[12rem] overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          >
            {cityCatalogLoading ? (
              <div
                className="flex items-center gap-2 px-3 py-2 font-onest text-sm text-slate-500"
                role="status"
              >
                <span className="size-3 animate-spin rounded-full border-2 border-slate-200 border-t-brand-500" />
                Загружаем города…
              </div>
            ) : cityCatalogError ? (
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="font-onest text-sm text-red-600" role="alert">
                  Не удалось загрузить города
                </span>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={retryCities}
                  className="shrink-0 font-onest text-xs font-medium text-brand-700 hover:underline"
                >
                  Повторить
                </button>
              </div>
            ) : searchResult.length > 0 ? (
              <ul>
                {searchResult.map((city) => (
                  <li key={city.id}>
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left font-onest text-sm text-slate-700 hover:bg-slate-50"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        commitValue(city.id);
                        setSearchTerm(city.name);
                        setCityOpen(false);
                        setActive(false);
                      }}
                    >
                      {city.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : searchTerm.trim() ? (
              <div
                className="px-3 py-2 font-onest text-sm text-slate-500"
                role="status"
              >
                Город не найден
              </div>
            ) : null}
          </div>
        ) : null}
      </span>
    );
  }

  if (isPhone) {
    const nationalDisplay = formatNationalPhonePart(
      parseStoredPhoneValue(value),
    );

    return (
      <span ref={wrapperRef} className="inline-flex items-baseline gap-0.5">
        <span className="select-none text-inherit">+7</span>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="tel"
          inputMode="numeric"
          value={nationalDisplay}
          placeholder="(999) 123-45-67"
          name={fieldName}
          autoComplete="tel-national"
          className={slotInputClass}
          style={{
            width: `${Math.max(12, nationalDisplay.length || 12)}ch`,
          }}
          onChange={(event) => {
            setValidationState(true);
            const { value: next } = formatNationalPhoneInput(
              event.target.value,
              event.target.selectionStart ?? event.target.value.length,
            );
            const national = parseStoredPhoneValue(next);
            commitValue(national ? formatPhoneNumber(national) : "");
          }}
          onBlur={closeEditor}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              closeEditor();
            }
          }}
        />
      </span>
    );
  }

  if (isDateField) {
    const applyDate = (iso: string, time = timeDraft, close = false) => {
      setValidationState(true);
      if (!iso) {
        commitValue("");
        setDateDraft("");
        setTimeDraft("");
      } else if (withTime) {
        const hm = displayTimeToHm(time) ? time : "00:00";
        commitValue(normalizeDateTimeSubmit(`${iso}T${hm}`));
        setDateDraft(isoToDisplayDate(iso));
        setTimeDraft(hm);
      } else {
        commitValue(iso);
        setDateDraft(isoToDisplayDate(iso));
      }
      if (close) closeEditor();
    };

    return (
      <span
        ref={wrapperRef}
        className="relative inline-flex items-baseline gap-1"
      >
        <input type="hidden" name={fieldName} value={value} />
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          inputMode="numeric"
          value={dateDraft}
          placeholder="ДД.ММ.ГГГГ"
          autoComplete="off"
          className={slotInputClass}
          style={{ width: "12ch" }}
          onChange={(event) => {
            const next = maskDisplayDate(event.target.value);
            setDateDraft(next);
            const iso = displayDateToIso(next);
            if (iso) applyDate(iso, timeDraft);
            if (!next) applyDate("");
          }}
          onBlur={(event) => {
            if (wrapperRef.current?.contains(event.relatedTarget as Node))
              return;
            if (dateDraft && !displayDateToIso(dateDraft)) {
              setDateDraft(isoToDisplayDate(value));
            }
            closeEditor();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              closeEditor();
            }
          }}
        />
        {withTime ? (
          <input
            type="text"
            inputMode="numeric"
            value={timeDraft}
            placeholder="ЧЧ:ММ"
            aria-label="Время"
            autoComplete="off"
            className={slotInputClass}
            style={{ width: "6ch" }}
            onChange={(event) => {
              const next = maskDisplayTime(event.target.value);
              setTimeDraft(next);
              const iso = displayDateToIso(dateDraft) ?? isoDatePart(value);
              if (iso && displayTimeToHm(next)) applyDate(iso, next);
            }}
            onBlur={(event) => {
              if (wrapperRef.current?.contains(event.relatedTarget as Node))
                return;
              closeEditor();
            }}
          />
        ) : null}
        <div
          role="dialog"
          aria-label="Календарь"
          onMouseDown={(event) => event.preventDefault()}
          className="absolute left-0 top-full z-20 mt-1 w-[17.5rem] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-lg"
        >
          <DateCalendar
            value={value}
            min={min}
            max={max}
            onSelect={(iso) => applyDate(iso, timeDraft, true)}
            onToday={() =>
              applyDate(formatIsoDate(new Date()), timeDraft, true)
            }
            onClear={() => applyDate("", timeDraft, true)}
          />
        </div>
      </span>
    );
  }

  return (
    <span ref={wrapperRef} className="inline">
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type={inputType}
        value={value}
        placeholder={displayLabel}
        name={fieldName}
        autoComplete="off"
        className={slotInputClass}
        style={{
          width: `${Math.max(displayLabel.length, value.length || 4, 4)}ch`,
        }}
        onChange={(event) => {
          setValidationState(true);
          commitValue(event.target.value);
        }}
        onBlur={closeEditor}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            closeEditor();
          }
        }}
      />
    </span>
  );
});

InlineFieldControl.displayName = "InlineFieldControl";
